/* @path: /src/pages/shelbula/workspaces/ReportView.jsx */
// @version: 19.0 (File-Anchored, Provenance-Driven)
// @description: Issues grouped by FILE because that's how developers think.
//               Shows actual code snippets using provenance data.
//               Actions are deterministic fix chips from the semantic engine.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, Check, ChevronRight, Trash2, Shield, RefreshCw, 
    ArrowRight, GitBranch, Zap, Accessibility, 
    CheckCircle2, Activity, FileCode, AlertCircle,
    Box, Unplug, ChevronDown, Network, Minus, Loader,
    AlertTriangle, Code, Layers, Target, FileText,
    Import, Link2, Eye, Key, Package, LayoutGrid
} from 'lucide-react';
import { apiManager } from './apiManager';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════════════════════

const c = {
    bg: '#FAFAFA',
    panel: '#F4F4F5', 
    card: '#FFFFFF',
    border: '#E4E4E7',
    borderLight: '#EBEBEF',
    text: '#18181B',
    textMid: '#52525B',
    textMuted: '#71717A',
    textLight: '#A1A1AA',
    
    critical: '#DC2626',
    criticalBg: '#FEF2F2',
    high: '#EA580C',
    highBg: '#FFF7ED',
    medium: '#CA8A04',
    mediumBg: '#FEFCE8',
    low: '#65A30D',
    lowBg: '#F7FEE7',
    
    action: '#059669',
    actionBg: '#ECFDF5',
    actionBorder: '#A7F3D0',
};

const bucketThemes = {
    deadCode: { accent: '#71717A', bg: '#18181B', icon: Trash2, label: 'Dead Code' },
    unusedImports: { accent: '#F59E0B', bg: '#78350F', icon: Import, label: 'Unused Imports' },
    security: { accent: '#EF4444', bg: '#1C1917', icon: Shield, label: 'Security' },
    accessibility: { accent: '#A78BFA', bg: '#1E1B4B', icon: Accessibility, label: 'Accessibility' },
    circularDependencies: { accent: '#F97316', bg: '#1C1917', icon: RefreshCw, label: 'Circular Deps' },
    danglingHandlers: { accent: '#FB7185', bg: '#1C1917', icon: Link2, label: 'Dangling Handlers' },
    asyncViolations: { accent: '#2DD4BF', bg: '#042F2E', icon: Activity, label: 'Async Issues' },
    argumentMismatches: { accent: '#38BDF8', bg: '#0C4A6E', icon: Code, label: 'Arg Mismatches' },
    unreachableCode: { accent: '#A1A1AA', bg: '#27272A', icon: Minus, label: 'Unreachable' },
    controlFlowViolations: { accent: '#FB923C', bg: '#7C2D12', icon: GitBranch, label: 'Control Flow' },
    hardErrors: { accent: '#FCA5A5', bg: '#450A0A', icon: AlertTriangle, label: 'Hard Errors' },
    ui: { accent: '#E879F9', bg: '#4A044E', icon: LayoutGrid, label: 'UI Issues' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

const getFilename = (filepath) => {
    if (!filepath) return 'unknown';
    return filepath.split('/').pop();
};

const getDirectory = (filepath) => {
    if (!filepath) return '';
    const parts = filepath.split('/');
    parts.pop();
    return parts.join('/');
};

const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
};

const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
const getSeverityColor = (severity) => {
    const colors = {
        critical: { text: c.critical, bg: c.criticalBg },
        high: { text: c.high, bg: c.highBg },
        medium: { text: c.medium, bg: c.mediumBg },
        low: { text: c.low, bg: c.lowBg },
    };
    return colors[severity] || colors.medium;
};

// ═══════════════════════════════════════════════════════════════════════════════
// API RESPONSE TRANSFORMER
// Normalizes the SemanticEngineReport v4.0 structure
// ═══════════════════════════════════════════════════════════════════════════════

const transformApiResponse = (report) => {
    if (!report) return { buckets: {}, deadCode: null };
    
    const buckets = {};
    
    // UNUSED IMPORTS
    if (report.unusedImports?.issues?.length > 0) {
        buckets.unusedImports = report.unusedImports.issues.map(issue => ({
            id: issue.entityId,
            type: 'unusedImport',
            severity: 'low',
            title: `Unused import: ${issue.symbol}`,
            description: `Imported from '${issue.source}' but never used`,
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions || [{ type: 'DELETE', label: 'Remove import', deterministic: true }]
        }));
    }
    
    // SECURITY
    if (report.security?.issues?.length > 0) {
        buckets.security = report.security.issues.map(issue => ({
            id: issue.entityId,
            type: 'security',
            severity: issue.severity || 'high',
            title: `Dangerous sink: ${issue.sinkName}()`,
            description: issue.sourceValue ? `Tainted data flows to ${issue.sinkName}` : `Potential injection via ${issue.sinkName}`,
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // ACCESSIBILITY
    if (report.accessibility?.issues?.length > 0) {
        buckets.accessibility = report.accessibility.issues.map(issue => ({
            id: issue.entityId,
            type: 'accessibility',
            severity: issue.severity || 'medium',
            title: `<${issue.tagName}> missing: ${issue.missingAttributes?.join(', ')}`,
            description: issue.hasEventHandler ? 'Interactive element without accessible name' : 'Element missing accessibility attributes',
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // CIRCULAR DEPENDENCIES
    if (report.circularDependencies?.issues?.length > 0) {
        buckets.circularDependencies = report.circularDependencies.issues.map(issue => ({
            id: `circ_${issue.fileA?.id}_${issue.fileB?.id}`,
            type: 'circularDependency',
            severity: 'high',
            title: `Circular: ${getFilename(issue.fileA?.path)} ↔ ${getFilename(issue.fileB?.path)}`,
            description: `${issue.fileA?.linksToB} imports → / ← ${issue.fileB?.linksToA} imports`,
            file: issue.fileA?.path,
            fileA: issue.fileA,
            fileB: issue.fileB,
            actions: issue.actions
        }));
    }
    
    // DANGLING HANDLERS
    if (report.danglingHandlers?.issues?.length > 0) {
        buckets.danglingHandlers = report.danglingHandlers.issues.map(issue => ({
            id: issue.entityId,
            type: 'danglingHandler',
            severity: 'high',
            title: `${issue.handlerName} is not defined`,
            description: 'Event handler references a function that does not exist',
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // ASYNC VIOLATIONS
    if (report.asyncViolations?.issues?.length > 0) {
        buckets.asyncViolations = report.asyncViolations.issues.map(issue => ({
            id: issue.entityId,
            type: issue.type === 'AWAIT_IN_NON_ASYNC' ? 'awaitInNonAsync' : 'unawaitedAsync',
            severity: issue.severity || 'high',
            title: issue.type === 'AWAIT_IN_NON_ASYNC' 
                ? `await in non-async: ${issue.functionName}()`
                : `Unawaited: ${issue.functionName}()`,
            description: issue.type === 'AWAIT_IN_NON_ASYNC'
                ? 'Syntax error: await can only be used in async functions'
                : 'Async function called without await - result may be lost',
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // ARGUMENT MISMATCHES
    if (report.argumentMismatches?.issues?.length > 0) {
        buckets.argumentMismatches = report.argumentMismatches.issues.map(issue => ({
            id: issue.entityId,
            type: 'argumentMismatch',
            severity: 'medium',
            title: `${issue.functionName}() expects ${issue.expected}, got ${issue.actual}`,
            description: issue.actual < issue.expected 
                ? `Missing ${issue.expected - issue.actual} argument(s)`
                : `${issue.actual - issue.expected} extra argument(s)`,
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // UNREACHABLE CODE
    if (report.unreachableCode?.issues?.length > 0) {
        buckets.unreachableCode = report.unreachableCode.issues.map(issue => ({
            id: issue.entityId,
            type: 'unreachable',
            severity: 'medium',
            title: `Unreachable ${issue.statementType}`,
            description: `Code after ${issue.blockedBy} will never execute`,
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // CONTROL FLOW VIOLATIONS
    if (report.controlFlowViolations?.issues?.length > 0) {
        buckets.controlFlowViolations = report.controlFlowViolations.issues.map(issue => ({
            id: issue.entityId,
            type: 'controlFlow',
            severity: 'high',
            title: `${issue.functionName}() missing return`,
            description: issue.missingPath ? `No return in ${issue.missingPath} path` : 'Not all code paths return a value',
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // HARD ERRORS
    if (report.hardErrors?.issues?.length > 0) {
        buckets.hardErrors = report.hardErrors.issues.map(issue => ({
            id: issue.entityId,
            type: 'hardError',
            severity: issue.severity || 'critical',
            category: issue.category,
            title: issue.title,
            description: `This will crash at runtime`,
            file: issue.provenance?.file,
            provenance: issue.provenance,
            actions: issue.actions
        }));
    }
    
    // UI BUCKET
    if (report.ui?.hasUI) {
        const uiIssues = [];
        
        if (report.ui.missingKeys?.length > 0) {
            report.ui.missingKeys.forEach(issue => {
                uiIssues.push({
                    id: issue.entityId,
                    type: 'missingKey',
                    severity: 'medium',
                    title: `<${issue.tagName}> in loop missing key`,
                    description: issue.componentName ? `Inside ${issue.componentName}` : 'List item needs unique key prop',
                    file: issue.provenance?.file,
                    provenance: issue.provenance,
                    actions: issue.actions
                });
            });
        }
        
        if (report.ui.orphanFragments?.length > 0) {
            report.ui.orphanFragments.forEach(issue => {
                uiIssues.push({
                    id: issue.entityId,
                    type: 'orphanFragment',
                    severity: 'low',
                    title: `Unnecessary fragment wrapping <${issue.childTag}>`,
                    description: 'Fragment with single child can be removed',
                    file: issue.provenance?.file,
                    provenance: issue.provenance,
                    actions: issue.actions
                });
            });
        }
        
        if (report.ui.unwiredElements?.length > 0) {
            report.ui.unwiredElements.forEach(issue => {
                uiIssues.push({
                    id: issue.entityId,
                    type: 'unwiredElement',
                    severity: 'medium',
                    title: `<${issue.tagName}> has no event handler`,
                    description: issue.componentName ? `In ${issue.componentName}` : 'Interactive element does nothing when clicked',
                    file: issue.provenance?.file,
                    provenance: issue.provenance,
                    actions: issue.actions
                });
            });
        }
        
        if (report.ui.deadComponents?.length > 0) {
            report.ui.deadComponents.forEach(issue => {
                uiIssues.push({
                    id: issue.entityId,
                    type: 'deadComponent',
                    severity: 'low',
                    title: `${issue.componentName} is never rendered`,
                    description: 'Component defined but not used anywhere',
                    file: issue.provenance?.file,
                    provenance: issue.provenance,
                    actions: issue.actions
                });
            });
        }
        
        if (uiIssues.length > 0) {
            buckets.ui = uiIssues;
        }
    }
    
    // DEAD CODE (special structure)
    const deadCode = report.deadCode ? {
        totalEntities: report.deadCode.totalEntities || 0,
        totalBytes: report.deadCode.totalBytes || 0,
        affectedFiles: report.deadCode.affectedFiles || 0,
        clusters: (report.deadCode.clusters || []).map(cluster => ({
            id: cluster.clusterId,
            rootName: cluster.rootName,
            entities: cluster.entities.map(e => ({
                id: e.entityId,
                name: e.name,
                kind: e.kind,
                role: e.role,
                file: e.provenance?.file,
                provenance: e.provenance,
                byteSize: e.byteSize,
                actions: e.actions
            })),
            actions: cluster.actions
        })),
        isolated: (report.deadCode.isolated || []).map(e => ({
            id: e.entityId,
            name: e.name,
            kind: e.kind,
            role: e.role,
            file: e.provenance?.file,
            provenance: e.provenance,
            byteSize: e.byteSize,
            actions: e.actions
        }))
    } : null;
    
    return { buckets, deadCode, entryPoints: report.entryPoints };
};

// ═══════════════════════════════════════════════════════════════════════════════
// BUCKET SIDEBAR
// ═══════════════════════════════════════════════════════════════════════════════

const BucketItem = ({ bucketKey, theme, count, isSelected, onClick }) => {
    if (count === 0) return null;
    const Icon = theme.icon;
    
    return (
        <div 
            onClick={onClick} 
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
            style={{ 
                background: isSelected ? theme.bg : 'transparent', 
                border: `1px solid ${isSelected ? theme.accent + '40' : 'transparent'}` 
            }}
        >
            <Icon size={14} style={{ color: isSelected ? theme.accent : c.textMuted }} />
            <span className="text-xs font-medium flex-1" style={{ color: isSelected ? '#fff' : c.text }}>
                {theme.label}
            </span>
            <span 
                className="text-[10px] font-bold px-1.5 py-0.5 rounded" 
                style={{ background: isSelected ? theme.accent : c.panel, color: isSelected ? '#fff' : c.textMuted }}
            >
                {count}
            </span>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CODE SNIPPET (from provenance)
// ═══════════════════════════════════════════════════════════════════════════════

const CodeSnippet = ({ provenance, maxLines = 3 }) => {
    // In real implementation, this would fetch the actual code from the server
    // using provenance.file, provenance.start, provenance.end
    // For now, show the location info
    
    if (!provenance?.file) return null;
    
    const startLine = provenance.start ? Math.floor(provenance.start / 40) + 1 : null;
    const endLine = provenance.end ? Math.floor(provenance.end / 40) + 1 : null;
    
    return (
        <div 
            className="font-mono text-[11px] px-3 py-2 rounded-lg overflow-hidden"
            style={{ background: '#1F2937', color: '#9CA3AF' }}
        >
            <div className="flex items-center gap-2 text-[10px] mb-1" style={{ color: '#6B7280' }}>
                <FileText size={10} />
                <span>{getFilename(provenance.file)}</span>
                {startLine && (
                    <span style={{ color: '#4B5563' }}>
                        {startLine === endLine ? `L${startLine}` : `L${startLine}-${endLine}`}
                    </span>
                )}
            </div>
            <div style={{ color: '#D1D5DB' }}>
                {/* Placeholder - in production this shows actual code */}
                <span style={{ color: '#6B7280' }}>// Code at bytes {provenance.start}-{provenance.end}</span>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTION CHIP
// ═══════════════════════════════════════════════════════════════════════════════

const ActionChip = ({ action, onExecute, compact = false }) => {
    const isDeterministic = action.deterministic;
    const requiresInput = action.requiresInput;
    
    return (
        <button
            onClick={() => onExecute(action)}
            className={`flex items-center gap-1.5 rounded-lg font-medium transition-all ${compact ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
            style={{
                background: isDeterministic ? c.actionBg : '#FEF3C7',
                color: isDeterministic ? c.action : '#D97706',
                border: `1px solid ${isDeterministic ? c.actionBorder : '#FCD34D'}`
            }}
        >
            {isDeterministic ? <Zap size={10} /> : <AlertCircle size={10} />}
            <span>{action.label}</span>
            {requiresInput && <ChevronRight size={10} />}
        </button>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILE-GROUPED ISSUE LIST
// ═══════════════════════════════════════════════════════════════════════════════

const IssueCard = ({ issue, isSelected, onClick }) => {
    const severityColor = getSeverityColor(issue.severity);
    
    return (
        <div
            onClick={onClick}
            className="p-3 rounded-lg cursor-pointer transition-all"
            style={{
                background: isSelected ? c.card : 'transparent',
                border: `1.5px solid ${isSelected ? c.action : 'transparent'}`,
                boxShadow: isSelected ? '0 2px 8px rgba(5, 150, 105, 0.1)' : 'none'
            }}
        >
            <div className="flex items-start gap-2">
                <div 
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: severityColor.text }}
                />
                <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium leading-snug mb-1" style={{ color: c.text }}>
                        {issue.title}
                    </div>
                    <div className="text-[11px] leading-snug" style={{ color: c.textMuted }}>
                        {issue.description}
                    </div>
                    
                    {issue.actions?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {issue.actions.slice(0, 2).map((action, i) => (
                                <ActionChip key={i} action={action} compact onExecute={() => {}} />
                            ))}
                            {issue.actions.length > 2 && (
                                <span className="text-[10px] px-2 py-1" style={{ color: c.textLight }}>
                                    +{issue.actions.length - 2} more
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const FileGroup = ({ filepath, issues, selectedIssueId, onSelectIssue }) => {
    const [expanded, setExpanded] = useState(true);
    const filename = getFilename(filepath);
    const directory = getDirectory(filepath);
    
    const sortedIssues = useMemo(() => 
        [...issues].sort((a, b) => {
            // Sort by severity first
            const sevA = severityOrder[a.severity] ?? 99;
            const sevB = severityOrder[b.severity] ?? 99;
            if (sevA !== sevB) return sevA - sevB;
            // Then by position in file
            const posA = a.provenance?.start ?? 0;
            const posB = b.provenance?.start ?? 0;
            return posA - posB;
        }),
        [issues]
    );
    
    const severityCounts = useMemo(() => {
        const counts = { critical: 0, high: 0, medium: 0, low: 0 };
        issues.forEach(i => { if (counts[i.severity] !== undefined) counts[i.severity]++; });
        return counts;
    }, [issues]);
    
    return (
        <div className="rounded-xl overflow-hidden" style={{ background: c.card, border: `1px solid ${c.borderLight}` }}>
            <div 
                className="px-3 py-2.5 flex items-center gap-2 cursor-pointer"
                style={{ background: '#F9FAFB', borderBottom: expanded ? `1px solid ${c.borderLight}` : 'none' }}
                onClick={() => setExpanded(!expanded)}
            >
                <FileCode size={14} style={{ color: c.textMid }} />
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: c.text }}>{filename}</div>
                    {directory && (
                        <div className="text-[10px] truncate" style={{ color: c.textLight }}>{directory}</div>
                    )}
                </div>
                
                <div className="flex items-center gap-1">
                    {severityCounts.critical > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: c.criticalBg, color: c.critical }}>
                            {severityCounts.critical}
                        </span>
                    )}
                    {severityCounts.high > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: c.highBg, color: c.high }}>
                            {severityCounts.high}
                        </span>
                    )}
                    {(severityCounts.medium + severityCounts.low) > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: c.panel, color: c.textMuted }}>
                            {severityCounts.medium + severityCounts.low}
                        </span>
                    )}
                </div>
                
                <ChevronDown 
                    size={14} 
                    style={{ color: c.textMuted, transform: expanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform 0.15s' }}
                />
            </div>
            
            {expanded && (
                <div className="p-2 space-y-1">
                    {sortedIssues.map(issue => (
                        <IssueCard
                            key={issue.id}
                            issue={issue}
                            isSelected={selectedIssueId === issue.id}
                            onClick={() => onSelectIssue(issue)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const FileGroupedIssueList = ({ theme, issues, selectedIssue, onSelectIssue }) => {
    const fileGroups = useMemo(() => {
        const groups = new Map();
        issues.forEach(issue => {
            const file = issue.file || '(unknown file)';
            if (!groups.has(file)) groups.set(file, []);
            groups.get(file).push(issue);
        });
        
        // Sort files by highest severity issue in each
        return Array.from(groups.entries()).sort((a, b) => {
            const maxSevA = Math.min(...a[1].map(i => severityOrder[i.severity] ?? 99));
            const maxSevB = Math.min(...b[1].map(i => severityOrder[i.severity] ?? 99));
            return maxSevA - maxSevB;
        });
    }, [issues]);
    
    const Icon = theme.icon;
    
    return (
        <div className="h-full flex flex-col">
            <div className="px-4 py-3 flex items-center gap-2 flex-shrink-0" style={{ background: theme.bg }}>
                <Icon size={14} style={{ color: theme.accent }} />
                <span className="text-xs font-semibold text-white">{theme.label}</span>
                <span 
                    className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full" 
                    style={{ background: theme.accent + '25', color: theme.accent }}
                >
                    {issues.length}
                </span>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ background: c.panel }}>
                {fileGroups.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm" style={{ color: c.textMuted }}>
                        No issues found
                    </div>
                ) : (
                    fileGroups.map(([filepath, fileIssues]) => (
                        <FileGroup
                            key={filepath}
                            filepath={filepath}
                            issues={fileIssues}
                            selectedIssueId={selectedIssue?.id}
                            onSelectIssue={onSelectIssue}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESOLUTION PANEL
// ═══════════════════════════════════════════════════════════════════════════════

const ResolutionPanel = ({ issue, theme, onResolve, onSkip }) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [inputValue, setInputValue] = useState('');
    
    useEffect(() => {
        setSelectedAction(null);
        setInputValue('');
    }, [issue?.id]);
    
    if (!issue) return null;
    
    const severityColor = getSeverityColor(issue.severity);
    const actions = issue.actions || [];
    const deterministicActions = actions.filter(a => a.deterministic && !a.requiresInput);
    const inputActions = actions.filter(a => a.requiresInput);
    
    return (
        <div className="flex-1 p-6 overflow-y-auto" style={{ background: '#F8F9FA' }}>
            <div className="max-w-xl mx-auto">
                
                {/* Header */}
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                        <span 
                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: severityColor.bg, color: severityColor.text }}
                        >
                            {issue.severity}
                        </span>
                        <span className="text-[10px] font-medium" style={{ color: c.textMuted }}>
                            {issue.type}
                        </span>
                    </div>
                    <h1 className="text-lg font-semibold leading-tight" style={{ color: c.text }}>
                        {issue.title}
                    </h1>
                    <p className="text-sm mt-1" style={{ color: c.textMid }}>
                        {issue.description}
                    </p>
                </div>
                
                {/* Location */}
                <div className="mb-4">
                    <CodeSnippet provenance={issue.provenance} />
                </div>
                
                <div className="h-px mb-4" style={{ background: c.border }} />
                
                {/* One-click fixes */}
                {deterministicActions.length > 0 && (
                    <div className="mb-4">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex-1 h-px" style={{ background: c.border }} />
                            <span className="text-[10px] font-bold tracking-wider" style={{ color: c.textLight }}>
                                ONE-CLICK FIXES
                            </span>
                            <div className="flex-1 h-px" style={{ background: c.border }} />
                        </div>
                        
                        <div className="space-y-2">
                            {deterministicActions.map((action, i) => (
                                <button
                                    key={i}
                                    onClick={() => onResolve(issue.id, action)}
                                    className="w-full p-3 rounded-xl text-left transition-all flex items-center gap-3"
                                    style={{ 
                                        background: c.actionBg, 
                                        border: `1.5px solid ${c.actionBorder}` 
                                    }}
                                >
                                    <div 
                                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ background: c.action }}
                                    >
                                        <Zap size={14} color="white" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold" style={{ color: '#047857' }}>
                                            {action.label}
                                        </div>
                                        <div className="text-[11px]" style={{ color: c.action }}>
                                            Deterministic fix — click to apply
                                        </div>
                                    </div>
                                    <ChevronRight size={16} style={{ color: c.action }} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Actions requiring input */}
                {inputActions.length > 0 && (
                    <div className="mb-4">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="flex-1 h-px" style={{ background: c.border }} />
                            <span className="text-[10px] font-bold tracking-wider" style={{ color: c.textLight }}>
                                INPUT REQUIRED
                            </span>
                            <div className="flex-1 h-px" style={{ background: c.border }} />
                        </div>
                        
                        {inputActions.map((action, i) => (
                            <div key={i} className="mb-3">
                                <div 
                                    className="p-3 rounded-xl mb-2"
                                    style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}
                                >
                                    <div className="flex items-start gap-2">
                                        <AlertCircle size={14} style={{ color: '#D97706' }} className="mt-0.5 flex-shrink-0" />
                                        <div>
                                            <div className="text-xs font-medium" style={{ color: '#92400E' }}>
                                                {action.label}
                                            </div>
                                            <div className="text-[11px] mt-0.5" style={{ color: '#A16207' }}>
                                                We need your input to complete this fix
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <input
                                    type="text"
                                    value={selectedAction === action ? inputValue : ''}
                                    onChange={(e) => { setSelectedAction(action); setInputValue(e.target.value); }}
                                    placeholder={`Enter ${action.requiresInput.replace(/_/g, ' ')}...`}
                                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                                    style={{ background: c.card, border: `1.5px solid ${c.border}`, color: c.text }}
                                />
                                
                                {selectedAction === action && inputValue.trim() && (
                                    <button
                                        onClick={() => onResolve(issue.id, { ...action, inputValue })}
                                        className="mt-2 px-4 py-2 rounded-lg text-sm font-medium"
                                        style={{ background: theme.accent, color: 'white' }}
                                    >
                                        Apply Fix
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                
                {/* No actions available */}
                {actions.length === 0 && (
                    <div className="p-4 rounded-xl text-center" style={{ background: c.panel }}>
                        <AlertCircle size={24} style={{ color: c.textMuted }} className="mx-auto mb-2" />
                        <p className="text-sm" style={{ color: c.textMid }}>
                            No automated fix available for this issue.
                        </p>
                        <p className="text-xs mt-1" style={{ color: c.textMuted }}>
                            Manual intervention required.
                        </p>
                    </div>
                )}
                
                <div className="h-px my-4" style={{ background: c.border }} />
                
                {/* Skip */}
                <div className="flex justify-end">
                    <button 
                        onClick={() => onSkip(issue.id)}
                        className="text-xs flex items-center gap-1"
                        style={{ color: c.textMuted }}
                    >
                        Skip for now <ArrowRight size={10} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// DEAD CODE PANEL
// ═══════════════════════════════════════════════════════════════════════════════

const DeadCodeEntity = ({ entity, isSelected, onToggle }) => {
    const filename = getFilename(entity.file);
    const isRoot = entity.role === 'ROOT';
    
    return (
        <div 
            onClick={() => onToggle(entity.id)}
            className="flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer transition-all"
            style={{ 
                background: isSelected ? c.actionBg : 'transparent',
                border: `1px solid ${isSelected ? c.actionBorder : 'transparent'}`
            }}
        >
            <div 
                className="w-3.5 h-3.5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ 
                    borderColor: isSelected ? c.action : c.border, 
                    background: isSelected ? c.action : 'transparent' 
                }}
            >
                {isSelected && <Check size={8} color="white" strokeWidth={3} />}
            </div>
            
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <FileCode size={11} style={{ color: isRoot ? '#F97316' : c.textMuted }} />
                    <span className="text-xs font-mono font-medium" style={{ color: c.text }}>{entity.name}</span>
                    <span className="text-[9px] px-1 py-0.5 rounded" style={{ background: c.panel, color: c.textMuted }}>
                        {entity.kind}
                    </span>
                    {isRoot && (
                        <span className="text-[8px] px-1 py-0.5 rounded font-bold" style={{ background: '#FEF3C7', color: '#D97706' }}>ROOT</span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-mono truncate" style={{ color: c.textLight }}>
                        {filename}
                    </span>
                    <span className="text-[10px] ml-auto flex-shrink-0" style={{ color: c.textLight }}>
                        {formatBytes(entity.byteSize)}
                    </span>
                </div>
            </div>
        </div>
    );
};

const DeadCodeCluster = ({ cluster, selected, onToggle, onToggleAll }) => {
    const [expanded, setExpanded] = useState(true);
    const allSelected = cluster.entities.every(e => selected.has(e.id));
    const someSelected = cluster.entities.some(e => selected.has(e.id));
    const totalBytes = cluster.entities.reduce((sum, e) => sum + (e.byteSize || 0), 0);
    
    return (
        <div className="rounded-xl overflow-hidden" style={{ background: c.card, border: `1px solid ${c.borderLight}` }}>
            <div 
                className="p-3 cursor-pointer flex items-center gap-2"
                style={{ background: '#FAFAFA', borderBottom: expanded ? `1px solid ${c.borderLight}` : 'none' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div 
                    onClick={(e) => { e.stopPropagation(); onToggleAll(cluster.entities.map(e => e.id)); }}
                    className="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer"
                    style={{ 
                        borderColor: allSelected ? c.action : someSelected ? c.action : c.border, 
                        background: allSelected ? c.action : 'transparent' 
                    }}
                >
                    {allSelected && <Check size={10} color="white" strokeWidth={3} />}
                    {someSelected && !allSelected && <div className="w-2 h-0.5 rounded" style={{ background: c.action }} />}
                </div>
                
                <Network size={14} style={{ color: '#F97316' }} />
                <span className="text-xs font-semibold" style={{ color: c.text }}>
                    {cluster.rootName || `Cluster`}
                </span>
                <span className="text-[10px]" style={{ color: c.textMuted }}>
                    {cluster.entities.length} items
                </span>
                <span className="text-[10px] ml-auto" style={{ color: c.textLight }}>
                    {formatBytes(totalBytes)}
                </span>
                <ChevronDown 
                    size={14} 
                    style={{ color: c.textMuted, transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} 
                />
            </div>
            
            {expanded && (
                <div className="p-2">
                    {cluster.entities.map(entity => (
                        <DeadCodeEntity 
                            key={entity.id}
                            entity={entity}
                            isSelected={selected.has(entity.id)}
                            onToggle={onToggle}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const DeadCodePanel = ({ deadCode, onDelete }) => {
    const [selected, setSelected] = useState(new Set());
    const { clusters, isolated, totalEntities, totalBytes, affectedFiles } = deadCode;
    
    const allIds = useMemo(() => {
        const clusterIds = clusters.flatMap(c => c.entities.map(e => e.id));
        const isolatedIds = isolated.map(i => i.id);
        return [...clusterIds, ...isolatedIds];
    }, [clusters, isolated]);
    
    const toggle = (id) => setSelected(prev => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    
    const toggleMultiple = (ids) => setSelected(prev => {
        const n = new Set(prev);
        const allSelected = ids.every(id => n.has(id));
        ids.forEach(id => allSelected ? n.delete(id) : n.add(id));
        return n;
    });
    
    const selectAll = () => setSelected(selected.size === allIds.length ? new Set() : new Set(allIds));
    
    const selectedBytes = useMemo(() => {
        let bytes = 0;
        clusters.forEach(c => c.entities.forEach(e => { if (selected.has(e.id)) bytes += (e.byteSize || 0); }));
        isolated.forEach(i => { if (selected.has(i.id)) bytes += (i.byteSize || 0); });
        return bytes;
    }, [selected, clusters, isolated]);
    
    return (
        <div className="flex-1 flex flex-col overflow-hidden" style={{ background: c.panel }}>
            
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 flex items-center gap-4" style={{ borderBottom: `1px solid ${c.border}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#18181B' }}>
                    <Unplug size={20} style={{ color: '#71717A' }} />
                </div>
                <div className="flex-1">
                    <h1 className="text-base font-semibold" style={{ color: c.text }}>Dead Code</h1>
                    <p className="text-xs" style={{ color: c.textMuted }}>
                        {totalEntities} entities · {formatBytes(totalBytes)} · {affectedFiles} files
                    </p>
                </div>
                
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: '#18181B' }}>
                    <CheckCircle2 size={16} style={{ color: c.action }} />
                    <span className="text-xs text-white">
                        <strong>Provably safe</strong>
                        <span style={{ color: '#9CA3AF' }}> — zero connections to live code</span>
                    </span>
                </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-hidden flex">
                
                {/* Clusters */}
                <div className="flex-1 flex flex-col overflow-hidden" style={{ borderRight: `1px solid ${c.border}` }}>
                    <div className="flex-shrink-0 px-4 py-3 flex items-center gap-2" style={{ background: '#F9FAFB', borderBottom: `1px solid ${c.borderLight}` }}>
                        <Network size={14} style={{ color: '#F97316' }} />
                        <span className="text-xs font-semibold" style={{ color: c.text }}>Clusters</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#D97706' }}>
                            {clusters.reduce((s, c) => s + c.entities.length, 0)} items
                        </span>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {clusters.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-sm" style={{ color: c.textMuted }}>
                                No clusters found
                            </div>
                        ) : (
                            clusters.map(cluster => (
                                <DeadCodeCluster 
                                    key={cluster.id}
                                    cluster={cluster}
                                    selected={selected}
                                    onToggle={toggle}
                                    onToggleAll={toggleMultiple}
                                />
                            ))
                        )}
                    </div>
                </div>
                
                {/* Isolated */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-shrink-0 px-4 py-3 flex items-center gap-2" style={{ background: '#F9FAFB', borderBottom: `1px solid ${c.borderLight}` }}>
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: c.action }} />
                        <span className="text-xs font-semibold" style={{ color: c.text }}>Isolated</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: c.actionBg, color: c.action }}>
                            {isolated.length} items
                        </span>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-4">
                        {isolated.length === 0 ? (
                            <div className="flex items-center justify-center h-32 text-sm" style={{ color: c.textMuted }}>
                                No isolated items
                            </div>
                        ) : (
                            <div className="rounded-xl overflow-hidden" style={{ background: c.card, border: `1px solid ${c.borderLight}` }}>
                                {isolated.map((item, idx) => (
                                    <div 
                                        key={item.id}
                                        style={{ borderBottom: idx < isolated.length - 1 ? `1px solid ${c.borderLight}` : 'none' }}
                                    >
                                        <DeadCodeEntity entity={item} isSelected={selected.has(item.id)} onToggle={toggle} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-3 flex items-center justify-between" style={{ background: c.card, borderTop: `1px solid ${c.border}` }}>
                <button onClick={selectAll} className="flex items-center gap-2 text-xs" style={{ color: c.textMuted }}>
                    <div 
                        className="w-4 h-4 rounded border-2 flex items-center justify-center"
                        style={{ 
                            borderColor: selected.size === allIds.length ? c.action : c.border, 
                            background: selected.size === allIds.length ? c.action : 'transparent' 
                        }}
                    >
                        {selected.size === allIds.length && <Check size={10} color="white" strokeWidth={3} />}
                    </div>
                    Select all ({allIds.length})
                </button>
                
                {selected.size > 0 && (
                    <span className="text-xs font-medium" style={{ color: c.action }}>
                        {selected.size} selected · {formatBytes(selectedBytes)}
                    </span>
                )}
                
                <button 
                    onClick={() => { onDelete(Array.from(selected)); setSelected(new Set()); }} 
                    disabled={selected.size === 0}
                    className="px-6 py-2 rounded-lg font-semibold text-xs transition-all disabled:opacity-40"
                    style={{ 
                        background: selected.size > 0 ? c.action : c.panel, 
                        color: selected.size > 0 ? 'white' : c.textMuted,
                        boxShadow: selected.size > 0 ? '0 2px 8px rgba(5, 150, 105, 0.25)' : 'none'
                    }}
                >
                    {selected.size > 0 ? `Delete ${selected.size} items` : 'Select items to delete'}
                </button>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ReportView = ({ isOpen, onClose, workspace }) => {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedBucket, setSelectedBucket] = useState(null);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [resolvedIssues, setResolvedIssues] = useState(new Set());
    const [deletedDeadCode, setDeletedDeadCode] = useState(new Set());
    
    // Fetch report when modal opens
    useEffect(() => {
        if (!isOpen || !workspace?.id) return;
        
        const fetchReport = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('[ReportView] Fetching report for workspace:', workspace.id);
                const response = await apiManager.getSemanticEngineReport(workspace.id);
                console.log('[ReportView] Raw API response:', response);
                const transformed = transformApiResponse(response.report);
                console.log('[ReportView] Transformed data:', transformed);
                setData(transformed);
            } catch (err) {
                console.error('[ReportView] Failed to fetch report:', err);
                setError(err.message || 'Failed to load analysis report');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchReport();
    }, [isOpen, workspace?.id]);
    
    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedBucket(null);
            setSelectedIssue(null);
            setResolvedIssues(new Set());
            setDeletedDeadCode(new Set());
        }
    }, [isOpen]);
    
    // Filter resolved issues from each bucket
    const filteredBuckets = useMemo(() => {
        if (!data?.buckets) return {};
        const result = {};
        for (const [key, issues] of Object.entries(data.buckets)) {
            result[key] = issues.filter(i => !resolvedIssues.has(i.id));
        }
        return result;
    }, [data?.buckets, resolvedIssues]);
    
    // Filter deleted dead code
    const filteredDeadCode = useMemo(() => {
        if (!data?.deadCode) return null;
        return {
            ...data.deadCode,
            clusters: data.deadCode.clusters
                .map(c => ({ ...c, entities: c.entities.filter(e => !deletedDeadCode.has(e.id)) }))
                .filter(c => c.entities.length > 0),
            isolated: data.deadCode.isolated.filter(e => !deletedDeadCode.has(e.id))
        };
    }, [data?.deadCode, deletedDeadCode]);
    
    // Build bucket list
    const bucketList = useMemo(() => {
        const list = [];
        
        for (const [key, issues] of Object.entries(filteredBuckets)) {
            if (issues.length > 0 && bucketThemes[key]) {
                list.push({ key, theme: bucketThemes[key], count: issues.length });
            }
        }
        
        // Add dead code bucket
        if (filteredDeadCode) {
            const count = filteredDeadCode.clusters.reduce((s, c) => s + c.entities.length, 0) + filteredDeadCode.isolated.length;
            if (count > 0) {
                list.push({ key: 'deadCode', theme: bucketThemes.deadCode, count });
            }
        }
        
        return list;
    }, [filteredBuckets, filteredDeadCode]);
    
    const totalIssues = useMemo(() => 
        bucketList.reduce((sum, b) => sum + b.count, 0),
        [bucketList]
    );
    
    const activeTheme = selectedBucket ? bucketThemes[selectedBucket] : null;
    const activeIssues = selectedBucket && selectedBucket !== 'deadCode' ? filteredBuckets[selectedBucket] || [] : [];
    
    const handleSelectBucket = (key) => { 
        setSelectedBucket(key); 
        setSelectedIssue(null); 
    };
    
    const handleResolve = useCallback((issueId, action) => { 
        console.log('[ReportView] Resolving issue:', issueId, 'with action:', action);
        setResolvedIssues(prev => new Set([...prev, issueId])); 
        setSelectedIssue(null); 
    }, []);
    
    const handleSkip = useCallback((issueId) => {
        // Move to next issue in the list
        const currentIndex = activeIssues.findIndex(i => i.id === issueId);
        if (currentIndex < activeIssues.length - 1) {
            setSelectedIssue(activeIssues[currentIndex + 1]);
        } else {
            setSelectedIssue(null);
        }
    }, [activeIssues]);
    
    const handleDeleteDeadCode = useCallback((ids) => { 
        console.log('[ReportView] Deleting dead code:', ids);
        setDeletedDeadCode(prev => new Set([...prev, ...ids])); 
    }, []);
    
    // Keyboard navigation
    useEffect(() => {
        const handleEsc = (e) => { 
            if (e.key === 'Escape') { 
                if (selectedIssue) setSelectedIssue(null);
                else if (selectedBucket) setSelectedBucket(null);
                else onClose(); 
            }
        };
        if (isOpen) window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose, selectedIssue, selectedBucket]);
    
    if (!isOpen) return null;
    
    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: c.bg }}>
            
      {/* Header */}
<header className="h-11 flex items-center justify-between px-4 flex-shrink-0" style={{ borderBottom: `1px solid ${c.border}` }}>
    <div className="flex items-center gap-3">
        <span className="text-sm font-semibold" style={{ color: c.text }}>Evōk Semantic Engine</span>
        {!isLoading && !error && (
            <div className="flex items-center gap-2">
                {totalIssues > 0 ? (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: c.criticalBg, color: c.critical }}>
                        {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
                    </span>
                ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: c.actionBg, color: c.action }}>
                        Clean
                    </span>
                )}
                <span className="text-[10px]" style={{ color: c.textLight }}>
                    {bucketList.length} categories active
                </span>
            </div>
        )}
    </div>
    
    <div className="flex items-center gap-3">
        {/* Debug info - remove in production */}
        {data && (
            <div className="flex items-center gap-1 text-[9px] font-mono" style={{ color: c.textLight }}>
                <span>API:</span>
                <span style={{ color: data.buckets?.unusedImports?.length ? c.action : c.critical }}>imp</span>
                <span style={{ color: data.buckets?.security?.length ? c.action : c.critical }}>sec</span>
                <span style={{ color: data.buckets?.accessibility?.length ? c.action : c.critical }}>a11y</span>
                <span style={{ color: data.buckets?.hardErrors?.length ? c.action : c.critical }}>err</span>
                <span style={{ color: data.buckets?.ui?.length ? c.action : c.critical }}>ui</span>
            </div>
        )}
        
        <div className="w-px h-4" style={{ background: c.border }} />
        <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider" style={{ color: c.textLight }}>Project</span>
            <span className="text-xs font-medium" style={{ color: c.textMid }}>{workspace?.workspaceName || 'Workspace'}</span>
        </div>
        <div className="w-px h-4" style={{ background: c.border }} />
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: c.textMuted }}>
            <X size={16} />
        </button>
    </div>
</header>
            
            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex">
                {isLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3">
                        <Loader className="animate-spin" size={24} style={{ color: c.textMuted }} />
                        <p className="text-sm" style={{ color: c.textMuted }}>Analyzing codebase...</p>
                    </div>
                ) : error ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3">
                        <AlertCircle size={28} style={{ color: '#EF4444' }} />
                        <p className="text-sm" style={{ color: c.text }}>{error}</p>
                        <button 
                            onClick={() => window.location.reload()} 
                            className="text-xs px-4 py-2 rounded-lg"
                            style={{ background: c.panel, color: c.textMid }}
                        >
                            Retry
                        </button>
                    </div>
                ) : bucketList.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: c.actionBg }}>
                            <CheckCircle2 size={28} style={{ color: c.action }} />
                        </div>
                        <h2 className="text-lg font-semibold mb-1" style={{ color: c.text }}>All Clear</h2>
                        <p className="text-sm" style={{ color: c.textMuted }}>No issues found in this codebase.</p>
                    </div>
                ) : (
                    <>
                        {/* Bucket Sidebar */}
                        <div className="w-52 flex-shrink-0 p-3 space-y-1 overflow-y-auto" style={{ borderRight: `1px solid ${c.border}` }}>
                            {bucketList.map(b => (
                                <BucketItem 
                                    key={b.key} 
                                    bucketKey={b.key}
                                    theme={b.theme} 
                                    count={b.count} 
                                    isSelected={selectedBucket === b.key} 
                                    onClick={() => handleSelectBucket(b.key)} 
                                />
                            ))}
                        </div>
                        
                        {/* Content Area */}
                        <div className="flex-1 flex overflow-hidden">
                            <AnimatePresence mode="wait">
                                {!selectedBucket ? (
                                    <motion.div 
                                        key="empty" 
                                        initial={{ opacity: 0 }} 
                                        animate={{ opacity: 1 }} 
                                        className="flex-1 flex items-center justify-center" 
                                        style={{ background: c.panel }}
                                    >
                                        <p className="text-sm" style={{ color: c.textMuted }}>Select a category to begin.</p>
                                    </motion.div>
                                ) : selectedBucket === 'deadCode' ? (
                                    <motion.div 
                                        key="dead" 
                                        initial={{ opacity: 0 }} 
                                        animate={{ opacity: 1 }} 
                                        className="flex-1 flex"
                                    >
                                        <DeadCodePanel deadCode={filteredDeadCode} onDelete={handleDeleteDeadCode} />
                                    </motion.div>
                                ) : (
                                    <motion.div 
                                        key={selectedBucket} 
                                        initial={{ opacity: 0 }} 
                                        animate={{ opacity: 1 }} 
                                        className="flex-1 flex overflow-hidden"
                                    >
                                        {/* File-grouped issue list */}
                                        <div className="w-96 flex-shrink-0 flex flex-col" style={{ borderRight: `1px solid ${c.border}` }}>
                                            <FileGroupedIssueList 
                                                theme={activeTheme} 
                                                issues={activeIssues} 
                                                selectedIssue={selectedIssue}
                                                onSelectIssue={setSelectedIssue} 
                                            />
                                        </div>
                                        
                                        {/* Resolution panel */}
                                        {selectedIssue ? (
                                            <ResolutionPanel 
                                                issue={selectedIssue} 
                                                theme={activeTheme} 
                                                onResolve={handleResolve}
                                                onSkip={handleSkip}
                                            />
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center" style={{ background: c.panel }}>
                                                <p className="text-sm" style={{ color: c.textMuted }}>Select an issue to resolve.</p>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </>
                )}
            </main>
        </div>,
        document.body
    );
};

export default ReportView;